import { defineStore } from 'pinia'
import { apiClient } from '@/api/axios'
import { isEmbedded } from '@/utils/embed'

type ImageDto = {
  id: number
  url: string
  thumb?: string
  title?: string
}

type ProductDto = {
  id: number
  name?: string
  slug?: string
  sku?: string
  designer_config: any | null
  images: ImageDto[]
}

export const useAdminDesignerStore = defineStore('adminDesigner', {
  state: () => ({
    productId: 0,
    config: {} as any,          // editovateľný JSON config
    images: [] as ImageDto[],
    previewMeta: null as any,
    loading: false,
    error: null as string | null,

    // flagy/kontext
    embedded: isEmbedded(),
    readonly: isEmbedded() && !!(window as any).DESIGNER_READONLY,
  }),

  getters: {
    hasImages: (s) => s.images && s.images.length > 0,
  },

  actions: {
    /** Fast-first-paint: načítaj z inline dát vložených widgetom */
    bootstrapFromWindow() {
      const cfg: any = (window as any).LABELSIMPLY_CFG || {}
      const boot: any = (window as any).DESIGNER_BOOTSTRAP || {}

      this.embedded = isEmbedded()
      this.readonly = this.embedded && !!(window as any).DESIGNER_READONLY

      if (boot.productId != null) this.productId = parseInt(boot.productId, 10) || 0

      // preview meta
      if (boot.previewMeta && typeof boot.previewMeta === 'object') {
        this.previewMeta = boot.previewMeta
      } else if (typeof boot.previewMeta === 'string') {
        try {
          this.previewMeta = JSON.parse(boot.previewMeta)
        } catch {
          this.previewMeta = null
        }
      } else if (cfg.previewMeta && typeof cfg.previewMeta === 'object') {
        this.previewMeta = cfg.previewMeta
      } else if (typeof cfg.previewMeta === 'string') {
        try {
          this.previewMeta = JSON.parse(cfg.previewMeta)
        } catch {
          this.previewMeta = null
        }
      } else {
        this.previewMeta = null
      }

      // config
      if (boot.initialConfig && typeof boot.initialConfig === 'object') {
        this.config = boot.initialConfig
      } else if (typeof boot.initialConfig === 'string') {
        try {
          this.config = JSON.parse(boot.initialConfig)
        } catch {
          this.config = {}
        }
      } else if (cfg.initialConfig && typeof cfg.initialConfig === 'object') {
        this.config = cfg.initialConfig
      } else if (typeof cfg.initialConfig === 'string') {
        try {
          this.config = JSON.parse(cfg.initialConfig)
        } catch {
          this.config = {}
        }
      } else {
        this.config = {}
      }

      // obrázky (ak widget poslal)
      if (Array.isArray(cfg.images)) {
        this.images = cfg.images
      }
    },

    client() {
      return apiClient(this.embedded)
    },

    async fetchProduct(forceId?: number) {
      this.loading = true
      this.error = null
      try {
        const id = forceId ?? this.productId
        if (!id) throw new Error('Missing productId')

        const { data } = await this.client().get<ProductDto>(`/product/${id}`)
        this.productId = data.id
        this.images = Array.isArray(data.images) ? data.images : []
        this.config = data.designer_config ?? {}
      } catch (e: any) {
        this.error = e?.message || 'Unknown error'
      } finally {
        this.loading = false
      }
    },

    async saveConfig() {
      const { data } = await this.client().post(`/product/${this.productId}/designer`, {
        designer_config: this.config,
      })
      return data
    },
  },
})
